import {
	AtSign,
	Check,
	HeartHandshake,
	MapPin,
	ShieldCheck,
	UserRound,
	type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import type { PersonDetail } from "@/api/db/people";
import { DraftField } from "@/components/cadastro/draft-field";
import { CadastroMultiField } from "@/components/cadastro/multi-field";
import { TriStateField } from "@/components/cadastro/tri-state-field";
import { maskPhone } from "@/components/wizard/masks";
import { CONSENT_FIELDS, type CadastroDraft } from "@/lib/cadastro-draft";
import { djs } from "@/lib/djs";
import { format } from "@/lib/format";
import { CadastroLabels, PessoaLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";

import { fieldProblems } from "../-utils/field-problems";
import { FichaFieldConflict } from "./ficha-field-conflict";

const REG = PessoaLabels.registry;

function RegistroSection({
	icon: Icon,
	title,
	children,
	className,
}: {
	icon: LucideIcon;
	title: string;
	children: ReactNode;
	className?: string;
}) {
	return (
		<section className={cn("min-w-0 p-5 sm:p-6", className)}>
			<div className="mb-5 flex items-center gap-3">
				<span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary-soft text-primary">
					<Icon className="size-4" />
				</span>

				<h2 className="type-section-title text-foreground">{title}</h2>
			</div>

			{children}
		</section>
	);
}

function ConsentChip({
	label,
	granted,
	onToggle,
	slot,
}: {
	label: string;
	granted: boolean;
	onToggle: () => void;
	slot: string;
}) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={granted}
			data-slot={slot}
			onClick={onToggle}
			className={cn(
				"inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
				granted
					? "border-success/20 bg-success-soft text-success"
					: "border-border bg-background/50 text-muted-foreground hover:bg-secondary",
			)}
		>
			<span
				className={cn(
					"grid size-4 place-items-center rounded-full border",
					granted ? "border-success bg-success text-card" : "border-border bg-card",
				)}
			>
				{granted && <Check className="size-2.5" />}
			</span>
			{label}
		</button>
	);
}

function since(person: PersonDetail) {
	const erp = person.erp_registered_at;
	const whatsapp = person.sendpulse_subscribed_at;

	if (erp && whatsapp) {
		if (djs(erp).valueOf() <= djs(whatsapp).valueOf()) {
			return { at: erp, note: REG.sinceErp };
		}

		return { at: whatsapp, note: REG.sinceWhatsApp };
	}

	if (erp) {
		return { at: erp, note: REG.sinceErp };
	}

	if (whatsapp) {
		return { at: whatsapp, note: REG.sinceWhatsApp };
	}

	return null;
}

export function FichaRegistroFields({ person }: { person: PersonDetail }) {
	const { control, setValue } = useFormContext<CadastroDraft>();
	const hasInsurance = useWatch({ control, name: "has_insurance" });
	const problems = fieldProblems(person);
	const enteredAt = since(person);

	return (
		<div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
			<div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(24rem,0.65fr)]">
				<RegistroSection
					icon={UserRound}
					title={REG.identitySection}
					className="border-b border-border xl:border-b-0 xl:border-r"
				>
					<div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-2">
						<DraftField
							fieldKey="name"
							span
							labelAction={<FichaFieldConflict problem={problems.name} />}
						/>

						<DraftField
							fieldKey="cpf"
							labelAction={<FichaFieldConflict problem={problems.cpf} />}
						/>

						<DraftField
							fieldKey="birth_date"
							labelAction={<FichaFieldConflict problem={problems.birth_date} />}
							hint={person.age === null ? undefined : REG.years(person.age)}
						/>

						<DraftField fieldKey="sex" />

						<DraftField fieldKey="rg" labelAction={<FichaFieldConflict problem={problems.rg} />} />

						<DraftField
							fieldKey="marital_status"
							labelAction={<FichaFieldConflict problem={problems.marital_status} />}
						/>

						<DraftField
							fieldKey="occupation"
							labelAction={<FichaFieldConflict problem={problems.occupation} />}
						/>

						<DraftField
							fieldKey="mother_name"
							labelAction={<FichaFieldConflict problem={problems.mother_name} />}
						/>
					</div>
				</RegistroSection>

				<div className="grid min-w-0 grid-cols-1 sm:grid-cols-2 xl:grid-cols-1">
					<RegistroSection
						icon={MapPin}
						title={REG.addressSection}
						className="border-b border-border sm:border-b-0 sm:border-r xl:border-b xl:border-r-0"
					>
						<div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-2">
							<DraftField
								fieldKey="address_raw"
								span
								labelAction={<FichaFieldConflict problem={problems.address} />}
							/>

							<DraftField
								fieldKey="address_cep"
								labelAction={<FichaFieldConflict problem={problems.cep} />}
							/>

							<DraftField fieldKey="preferred_channel" />
						</div>
					</RegistroSection>

					<RegistroSection icon={AtSign} title={REG.contactSection}>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-2">
							<CadastroMultiField
								name="phones"
								label={CadastroLabels.field.phones}
								labelAction={<FichaFieldConflict problem={problems.phone} />}
								slot="phone"
								addLabel="Adicionar telefone"
								placeholder="(31) 99000-0000"
								type="tel"
								mask={maskPhone}
							/>

							<CadastroMultiField
								name="emails"
								label={CadastroLabels.field.emails}
								labelAction={<FichaFieldConflict problem={problems.email} />}
								slot="email"
								addLabel="Adicionar e-mail"
								placeholder="nome@exemplo.com"
								type="email"
							/>
						</div>
					</RegistroSection>
				</div>
			</div>

			<div className="grid grid-cols-1 border-t border-border xl:grid-cols-[minmax(0,1fr)_24rem]">
				<RegistroSection
					icon={HeartHandshake}
					title={REG.contextSection}
					className="border-b border-border xl:border-b-0 xl:border-r"
				>
					<div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
						<Controller
							control={control}
							name="has_insurance"
							render={({ field }) => (
								<TriStateField
									label={CadastroLabels.insurance_presence}
									labelAction={<FichaFieldConflict problem={problems.has_insurance} />}
									value={field.value}
									onChange={(value) => {
										field.onChange(value);

										if (value === false) {
											setValue("insurance", "", { shouldDirty: true });
										}
									}}
									slot="has-insurance"
								/>
							)}
						/>

						<DraftField
							fieldKey="insurance"
							disabled={hasInsurance === false}
							labelAction={<FichaFieldConflict problem={problems.insurance} />}
						/>

						<div className="sm:col-span-2">
							<DraftField fieldKey="how_found" />
						</div>
						<div className="sm:col-span-2">
							<DraftField fieldKey="how_found_detail" />
						</div>

						<div className="sm:col-span-2">
							<DraftField fieldKey="referred_by" />
						</div>
					</div>

					{enteredAt !== null && (
						<div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-4 text-sm">
							<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
								{REG.since}
							</span>
							<span className="font-medium text-foreground">{format.monthYear(enteredAt.at)}</span>
							<span className="text-muted-foreground">{enteredAt.note}</span>
						</div>
					)}
				</RegistroSection>

				<RegistroSection
					icon={ShieldCheck}
					title={REG.consentsHeading}
					className="bg-background/35"
				>
					<div className="flex flex-wrap gap-2">
						{CONSENT_FIELDS.map((consent) => (
							<Controller
								key={consent}
								control={control}
								name={consent}
								render={({ field }) => (
									<ConsentChip
										label={PessoaLabels.consentChip[consent]}
										granted={field.value === true}
										onToggle={() => field.onChange(field.value !== true)}
										slot={`consent-${consent}`}
									/>
								)}
							/>
						))}
					</div>
				</RegistroSection>
			</div>
		</div>
	);
}
